import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import Stack from '@mui/material/Stack';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { useActiveCompany } from '@/contexts/ActiveCompanyContext';
import ProcessConfigurationForm from '@/pages/administration/processConfiguration/ProcessConfigurationForm';
import type { ProcessConfigurationDto, UpdateProcessConfigurationRequest } from '@/types/configuration';
import type { CompanyDto } from '@/types/organization';

/** SuperAdmin-only counterpart to ProcessConfigurationPage — same form, but scoped to whichever
 * company was picked from the Companies console (via the company/{companyId} routes) instead of
 * the acting user's own company. Reached from a company row's "Process Config" action. */
export default function CompanyProcessConfigurationPage() {
  const { t } = useTranslation('processConfiguration');
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: company } = useQuery({
    queryKey: ['companies', companyId],
    queryFn: async () => (await apiClient.get<CompanyDto>(`/companies/${companyId}`)).data,
    enabled: Boolean(companyId),
  });

  const { setActiveCompany } = useActiveCompany();
  useEffect(() => {
    setActiveCompany(company ? { id: company.id, name: company.name } : null);
    return () => setActiveCompany(null);
  }, [company, setActiveCompany]);

  // Query key must exactly match useProcessConfiguration(companyId)'s — that's the same hook
  // AppShell uses to gate the left nav while a SuperAdmin is viewing this company (see
  // ActiveCompanyContext). A mismatched key here would leave the nav reading a stale, separately-
  // cached copy of the config until its own background refetch catches up, instead of updating the
  // instant this page saves.
  const { data, isLoading } = useQuery({
    queryKey: ['process-configuration', 'company', companyId],
    queryFn: async () => (await apiClient.get<ProcessConfigurationDto>(`/process-configuration/company/${companyId}`)).data,
    enabled: Boolean(companyId),
  });

  const [form, setForm] = useState<ProcessConfigurationDto | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateProcessConfigurationRequest) =>
      apiClient.put<ProcessConfigurationDto>(`/process-configuration/company/${companyId}`, payload),
    onSuccess: (response) => {
      setForm(response.data);
      queryClient.setQueryData(['process-configuration', 'company', companyId], response.data);
    },
  });

  if (isLoading || !form) {
    return null;
  }

  const set = <K extends keyof ProcessConfigurationDto>(key: K, value: ProcessConfigurationDto[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = () => {
    if (!form) return;
    const { id: _id, companyId: _companyId, ...payload } = form;
    saveMutation.mutate(payload);
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Stack direction="row" spacing={1.5}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/companies')}>
              {t('backToCompanies')}
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saveMutation.isPending}>
              {t('save')}
            </Button>
          </Stack>
        }
      />

      <ProcessConfigurationForm form={form} set={set} />
    </>
  );
}
